import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', success: '#10B981', warning: '#F59E0B',
  error: '#EF4444', grey: '#6B7280', border: '#E5E7EB', bg: '#F8FAFC', white: '#FFFFFF',
};

const STATUS = {
  not_started:  { label: 'Ready to start', color: C.teal,    bg: '#E0F7FC' },
  released:     { label: 'Ready to start', color: C.teal,    bg: '#E0F7FC' },
  in_progress:  { label: 'In progress',     color: C.warning, bg: '#FEF3C7' },
  submitted:    { label: 'Awaiting marking', color: '#3B82F6', bg: '#EFF6FF' },
  marked:       { label: 'Marked',           color: C.success, bg: '#D1FAE5' },
};

export default function MyMockTests() {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payInfo, setPayInfo] = useState(null);   // bank/local payment details
  const [expandedId, setExpandedId] = useState(null);

  // Booking flow
  const [showBook, setShowBook] = useState(false);
  const [types, setTypes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [selType, setSelType] = useState(null);
  const [selSlot, setSelSlot] = useState(null);
  const [bookErr, setBookErr] = useState('');
  const [booking, setBooking] = useState(false);

  const loadTests = () => {
    api.get('/api/student/mock-tests/my-tests')
      .then(r => setTests(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTests([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadTests(); }, []);
  useEffect(() => { api.get('/api/student/payment-info').then(r => setPayInfo(r.data)).catch(() => {}); }, []);

  const openBook = async () => {
    setShowBook(true); setSelType(null); setSelSlot(null); setBookErr(''); setSlots([]);
    try { const r = await api.get('/api/student/mock-tests/types'); setTypes(Array.isArray(r.data) ? r.data : []); }
    catch { setTypes([]); }
  };
  const pickType = async (t) => {
    setSelType(t); setSelSlot(null); setSlots([]); setBookErr('');
    try { const r = await api.get('/api/student/mock-tests/slots?test_type_id=' + t.id); setSlots(Array.isArray(r.data) ? r.data : []); }
    catch { setSlots([]); }
  };
  const confirmBook = async () => {
    if (!selType || !selSlot) { setBookErr('Choose a test and a time slot.'); return; }
    setBooking(true); setBookErr('');
    try {
      await api.post('/api/student/mock-tests/book', { test_type_id: selType.id, slot_id: selSlot.id });
      setShowBook(false);
      loadTests();
    } catch (e) { setBookErr(e.response?.data?.error || 'Could not book. Please try again.'); }
    setBooking(false);
  };
  const fmtSlot = (s) => {
    try { return new Date(s.slot_date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) + ' · ' + (s.slot_time || '').slice(0,5) + (s.room_location ? ` · ${s.room_location}` : ''); }
    catch { return s.slot_date; }
  };

  const downloadCert = async (code) => {
    try {
      const r = await api.get('/api/certificates/' + code + '/download', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { alert('Could not download certificate'); }
  };

  const examName = (b) => b.test_type_name || (b.test_type && b.test_type.name) || 'Mock Test';

  return (
    <div style={{ minHeight: '100vh', background: C.bg, padding: '32px 20px' }}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ color: C.navy, fontSize: 26, fontWeight: 800, margin: '0 0 4px' }}>My Mock Tests</h1>
            <p style={{ color: C.grey, margin: '0 0 24px' }}>Book, take and review your mock tests. Each booking is paid separately.</p>
          </div>
          <button onClick={openBook} style={{ padding: '11px 20px', borderRadius: 10, background: C.navy, color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>+ Book a Mock Test</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', color: C.grey, padding: 40 }}>Loading…</div>
        ) : tests.length === 0 ? (
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 14, padding: 48, textAlign: 'center', color: C.grey }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🧪</div>
            You have no mock tests yet. Once your booking is confirmed and activated by the centre, it will appear here.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {tests.map((b) => {
              const sess = b.session;
              const st = sess && STATUS[sess.status];
              const paid = b.payment_status === 'paid';
              const cert = sess && sess.certificate;
              const bd = payInfo?.bank_details || {};
              return (
                <div key={b.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: C.navy, fontSize: 16 }}>{examName(b)}</div>
                    <div style={{ color: C.grey, fontSize: 13, marginTop: 4 }}>
                      {b.slot_date ? `Slot: ${b.slot_date} ${b.slot_time || ''}` : 'Walk-in / unscheduled'}
                      {!paid && <span style={{ color: C.error, marginLeft: 8 }}>· Payment pending</span>}
                    </div>
                    {sess && (sess.status === 'submitted' || sess.status === 'marked') && (
                      <div style={{ color: C.navy, fontSize: 13, marginTop: 6 }}>
                        {sess.band_score ? <>Overall: <strong>{sess.band_score}</strong></> : null}
                        {/* Per-section breakdown */}
                        {sess.section_scores && Object.keys(sess.section_scores).length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                            {Object.entries(sess.section_scores).map(([name, v]) => (
                              <span key={name} style={{ fontSize: 12, background: '#F1F5F9', borderRadius: 6, padding: '3px 8px', color: C.navy }}>
                                {name}: <strong>{v && v.band != null ? Number(v.band).toFixed(1) : (typeof v === 'object' && v.percent != null ? `${Math.round(v.percent)}%` : String(v))}</strong>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {st && (
                      <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: st.bg, color: st.color }}>{st.label}</span>
                    )}
                    {!sess && (
                      <span style={{ fontSize: 12, color: C.grey }}>Awaiting activation by centre</span>
                    )}
                    {sess && (sess.status === 'not_started' || sess.status === 'released') && paid && (
                      <button onClick={() => navigate('/mock-test/' + sess.id)} style={{ padding: '9px 18px', borderRadius: 8, background: C.teal, color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer' }}>Start Test →</button>
                    )}
                    {sess && sess.status === 'in_progress' && (
                      <button onClick={() => navigate('/mock-test/' + sess.id)} style={{ padding: '9px 18px', borderRadius: 8, background: C.warning, color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer' }}>Resume →</button>
                    )}
                    {sess && (sess.status === 'submitted' || sess.status === 'marked') && (
                      <button onClick={() => navigate('/mock-test/' + sess.id)} style={{ padding: '9px 18px', borderRadius: 8, background: C.teal, color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer' }}>View Results →</button>
                    )}
                    {cert && cert.verification_code && (
                      <button onClick={() => downloadCert(cert.verification_code)} style={{ padding: '9px 16px', borderRadius: 8, background: '#fff', color: C.teal, border: `2px solid ${C.teal}`, fontWeight: 700, cursor: 'pointer' }}>⬇ Certificate</button>
                    )}
                  </div>
                 </div>
                 {!paid && (bd.local_payment || bd.account_number) && (
                   <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                     <button onClick={() => setExpandedId(expandedId === b.id ? null : b.id)} style={{ background: 'none', border: 'none', color: C.teal, fontWeight: 700, cursor: 'pointer', fontSize: 13, padding: 0 }}>
                       💳 How to pay {expandedId === b.id ? '▲' : '▾'}
                     </button>
                     {expandedId === b.id && (
                       <div style={{ marginTop: 10 }}>
                         {bd.local_payment && (
                           <div style={{ background: '#ECFDF5', border: '2px solid #10B981', borderRadius: 10, padding: 12, marginBottom: 10 }}>
                             <div style={{ fontWeight: 800, fontSize: 13, color: '#065F46', marginBottom: 4 }}>💳 Pay by local card — fastest</div>
                             <div style={{ fontSize: 13, color: C.navy, fontWeight: 600 }}>{bd.local_payment}</div>
                           </div>
                         )}
                         {bd.account_number && (
                           <div style={{ background: '#F8FAFC', border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, fontSize: 13, lineHeight: 1.7 }}>
                             {bd.bank_name && <div><b>Bank:</b> {bd.bank_name}</div>}
                             {bd.account_holder && <div><b>Account holder:</b> {bd.account_holder}</div>}
                             {bd.account_number && <div><b>Account number:</b> {bd.account_number}</div>}
                             {bd.swift_bic && <div><b>SWIFT / BIC:</b> {bd.swift_bic}</div>}
                             {bd.payment_notes && <div style={{ color: C.grey, marginTop: 4 }}>{bd.payment_notes}</div>}
                           </div>
                         )}
                         <div style={{ fontSize: 12, color: C.grey, marginTop: 8 }}>After paying, the centre confirms your payment and activates your test. Please use your full name as the reference.</div>
                       </div>
                     )}
                   </div>
                 )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showBook && (
        <div onClick={() => !booking && setShowBook(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(13,27,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: C.white, borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '88vh', overflowY: 'auto', padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ color: C.navy, fontSize: 20, fontWeight: 800, margin: 0 }}>Book a Mock Test</h2>
              <button onClick={() => !booking && setShowBook(false)} style={{ background: 'none', border: 'none', fontSize: 24, color: C.grey, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ fontWeight: 700, color: C.navy, fontSize: 14, marginBottom: 8 }}>1. Choose a test</div>
            {types.length === 0 ? (
              <div style={{ color: C.grey, fontSize: 14, marginBottom: 16 }}>No mock tests are available at your centre right now.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                {types.map((t) => {
                  const active = selType && selType.id === t.id;
                  return (
                    <button key={t.id} onClick={() => pickType(t)} style={{ textAlign: 'left', padding: '12px 14px', borderRadius: 10, border: `2px solid ${active ? C.teal : C.border}`, background: active ? '#E0F7FC' : C.white, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, color: C.navy }}>{t.name}</span>
                      <span style={{ fontWeight: 800, color: C.teal }}>${Number(t.price_usd || 0).toFixed(2)}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {selType && (
              <>
                <div style={{ fontWeight: 700, color: C.navy, fontSize: 14, marginBottom: 8 }}>2. Choose a time slot</div>
                {slots.length === 0 ? (
                  <div style={{ color: C.grey, fontSize: 14, marginBottom: 16 }}>No available slots in the next few months. Please check back later.</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                    {slots.map((s) => {
                      const active = selSlot && selSlot.id === s.id;
                      return (
                        <button key={s.id} onClick={() => setSelSlot(s)} style={{ padding: '10px 14px', borderRadius: 10, border: `2px solid ${active ? C.teal : C.border}`, background: active ? '#E0F7FC' : C.white, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: C.navy }}>
                          {fmtSlot(s)}
                          {typeof s.available === 'number' && <span style={{ color: C.grey, fontWeight: 500 }}> · {s.available} left</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {bookErr && <div style={{ color: C.error, fontSize: 13, marginBottom: 12 }}>{bookErr}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
              <button onClick={() => !booking && setShowBook(false)} style={{ padding: '10px 18px', borderRadius: 8, background: '#fff', color: C.grey, border: `1px solid ${C.border}`, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button disabled={!selType || !selSlot || booking} onClick={confirmBook} style={{ padding: '10px 20px', borderRadius: 8, background: (!selType || !selSlot || booking) ? '#9CA3AF' : C.teal, color: '#fff', border: 'none', fontWeight: 700, cursor: (!selType || !selSlot || booking) ? 'default' : 'pointer' }}>{booking ? 'Booking…' : 'Confirm Booking'}</button>
            </div>
            <p style={{ color: C.grey, fontSize: 12, margin: '12px 0 0' }}>After booking, payment is collected by your centre. Your test appears below once confirmed.</p>
          </div>
        </div>
      )}
    </div>
  );
}
